import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { from, map, Observable, take } from 'rxjs';
import { InvitedSectionType } from 'src/app/schedule/event-properties/event-attendees/event-attendees.component';
import { DataSourceFetchParams, DataSourceFetchResult } from 'src/app/services/data-source.service';
import { FiltersFactoryService } from 'src/app/services/filters-factory.service';
import { ProfilesService } from 'src/app/services/profiles.service';
import {
  FilterData,
  FilterKeyValuePair,
  IFilterAPI,
} from 'src/app/standalones/components/generic-filters-view/filters.interface';
import {
  ConfirmationModalComponent,
  ModalStandardTemplateEnum,
} from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { MultiSelectionListModalSections } from 'src/app/standalones/components/modals/selection-modal/multi-selection-list-modal/multi-selection-list-modal.component';
import { User } from '../../models/user';

export interface SelectUsersDialogData {
  invitedSectionType: InvitedSectionType;
  contentHeader?: string;
  initialFilters?: FilterKeyValuePair[];
}

interface InvitedSectionTypeTranslations {
  title: string;
  contentHeader: string;
  searchPlaceholder: string;
  emptyMessage: string;
}

@Component({
  selector: 'app-select-users-dialog',
  templateUrl: './select-users-dialog.component.html',
  styleUrls: ['./select-users-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectUsersDialogComponent implements OnInit {
  @ViewChild('userHeader', { static: true }) userHeader!: TemplateRef<void>;
  @ViewChild('userCell', { static: true }) userCell!: TemplateRef<{ $implicit: User }>;

  filters: Observable<FilterData[]>;
  columns!: MultiSelectionListModalSections<User>['columns'];
  translations: InvitedSectionTypeTranslations;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectUsersDialogData,
    private dialogRef: MatDialogRef<SelectUsersDialogComponent>,
    private profilesService: ProfilesService,
    private filtersFactoryService: FiltersFactoryService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    this.fetch = this.fetch.bind(this);
    this.filters = this.filtersFactoryService.createProfilesFilters(this.data.initialFilters ?? []);
    this.translations = this.invitedSectionTypeTranslations[this.data.invitedSectionType];
  }

  get invitedSectionTypeTranslations(): Record<InvitedSectionType, InvitedSectionTypeTranslations> {
    return {
      [InvitedSectionType.ATTENDEE]: {
        title: this.translate.instant('Select attendees'),
        contentHeader: this.translate.instant('Attendees'),
        searchPlaceholder: this.translate.instant('Search for attendee'),
        emptyMessage: this.translate.instant('No attendees found'),
      },
      [InvitedSectionType.ORGANIZER]: {
        title: this.translate.instant('Select organizers'),
        contentHeader: this.translate.instant('Organizers'),
        searchPlaceholder: this.translate.instant('Search for organizer'),
        emptyMessage: this.translate.instant('No organizers found'),
      },
      [InvitedSectionType.AUTO_ADDED]: {
        title: this.translate.instant('Select users'),
        contentHeader: this.translate.instant('Users'),
        searchPlaceholder: this.translate.instant('Search for user'),
        emptyMessage: this.translate.instant('No users found'),
      },
    };
  }

  ngOnInit(): void {
    this.columns = {
      user: { header: this.userHeader, cell: this.userCell },
    };
  }

  fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<User>> {
    const filters: IFilterAPI = { ...params.filter, sortBy: 'name-asc' };

    return from(
      this.profilesService.getInstitutionUsers(params.pageIndex + 1, params.pageSize, filters),
    ).pipe(
      take(1),
      map((res) => ({
        items: res.users,
        totalItems: res.total,
      })),
    );
  }

  cancel(items: User[]): void {
    if (items.length) {
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            type: ModalStandardTemplateEnum.UNSAVED_CHANGES,
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((output: 'confirm' | 'cancel') => {
          if (output === 'confirm') {
            this.closeDialog();
          }
        });
    } else {
      this.closeDialog();
    }
  }

  submitAndClose = (items: User[]): void => {
    this.dialogRef.close(items);
  };

  closeDialog = (): void => {
    this.dialogRef.close();
  };
}
